import { data as dataResponse, useRouteLoaderData } from 'react-router';
import { captureException } from '@dinewise/logger/server';
import { PaymentsAppShell } from '~/components/payments-app-shell.tsx';
import type { RootLoader } from '~/root.tsx';
import { getAppLoadContext } from '~/utils/app-load-context.server.ts';
import { getTokenGQLClient } from '~/utils/graphql/gql.server.ts';
import { getProfilePictureSources } from '~/utils/images.server.ts';
import { isSafeRedirectDestination } from '~/utils/login-params.ts';
import { ensureAuthenticated } from '~/utils/sessions.server.ts';
import type { Route } from './+types/index.ts';
import AuthenticatedHome from './authenticated-home.tsx';
import {
  fetchHomeReservations,
  homeReservationsHasMore,
} from './fetch-home-reservations.server.ts';
import LoginScreen from './login-screen.tsx';

export const loader = async ({ request, context }: Route.LoaderArgs) => {
  const { user, token } = getAppLoadContext(context);
  const url = new URL(request.url);
  const destinationParam = url.searchParams.get('destination');
  const destination = isSafeRedirectDestination(destinationParam) ? destinationParam : null;
  const avatarImageSources = user ? getProfilePictureSources(user.id, user.profilePictureId) : null;

  if (!user || !token) {
    return {
      user,
      destination,
      avatarImageSources,
      venues: [],
      venueGroupName: null,
      preferredVenueIds: [],
      reservations: [],
      reservationsHasMore: false,
    };
  }

  try {
    const gqlClient = getTokenGQLClient(token, request.signal);
    const [metadataResult, preferredVenuesResult] = await Promise.all([
      gqlClient.homeMetadata(),
      gqlClient.userVenuePortalPreferredVenues({ input: { userId: user.id } }),
    ]);

    const venueGroup = metadataResult.me?.venueGroup;
    const preferredVenueIds =
      preferredVenuesResult.userVenuePortalPreferredVenues?.preferredVenueIds ?? [];
    const selectedVenueId = preferredVenueIds[0] ?? null;
    const reservations = await fetchHomeReservations({
      gqlClient,
      skip: 0,
      venueIds: selectedVenueId ? [selectedVenueId] : [],
      timezone: user.timezone,
      signal: request.signal,
    });

    return {
      user,
      destination,
      avatarImageSources,
      venues: venueGroup?.venues ?? [],
      venueGroupName: venueGroup?.name ?? null,
      preferredVenueIds,
      reservations,
      reservationsHasMore: homeReservationsHasMore(reservations),
      locale: user.locale,
      timezone: user.timezone,
    };
  } catch (error) {
    captureException({
      user,
      error,
      message: 'Failed to load home page',
      properties: { route: '_index' },
    });

    throw dataResponse(null, { status: 500 });
  }
};

export const action = async ({ request, context }: Route.ActionArgs) => {
  const { user, token } = await ensureAuthenticated(request, context);

  const formData = await request.formData();
  const subaction = formData.get('subaction');

  if (subaction === 'venueFilter') {
    const clearValue = formData.get('clear');
    const shouldClear = clearValue instanceof File ? false : clearValue === 'true';
    const venueIdValue = formData.get('venueId');
    const venueId = venueIdValue instanceof File ? null : venueIdValue;

    if (!shouldClear && (!venueId || venueId.length === 0)) {
      return dataResponse({ error: 'Invalid venue' }, { status: 400 });
    }

    const gqlClient = getTokenGQLClient(token, request.signal);

    try {
      const preferredVenueIds = shouldClear || !venueId ? [] : [venueId];

      await gqlClient.upsertVenuePortalPreferences({
        input: {
          userId: user.id,
          preferredVenueIds,
        },
      });
    } catch (error) {
      captureException({
        user,
        error,
        message: 'Failed to update home venue filter',
        properties: { route: '_index', subaction: 'venueFilter' },
      });
      throw error;
    }

    return dataResponse({ result: 'success' });
  }

  return dataResponse({ error: 'Unknown action' }, { status: 400 });
};

const Home = ({ loaderData }: Route.ComponentProps) => {
  const rootData = useRouteLoaderData<RootLoader>('root');
  const termsUrl = `${rootData?.webBaseUrl ?? ''}/terms`;
  const {
    user,
    destination,
    avatarImageSources,
    venues,
    venueGroupName,
    preferredVenueIds,
    reservations,
    reservationsHasMore,
    locale,
    timezone,
  } = loaderData;

  if (user) {
    return (
      <PaymentsAppShell user={user} avatarImageSources={avatarImageSources}>
        <AuthenticatedHome
          venues={venues}
          venueGroupName={venueGroupName}
          preferredVenueIds={preferredVenueIds}
          reservations={reservations}
          reservationsHasMore={reservationsHasMore}
          locale={locale}
          timezone={timezone}
        />
      </PaymentsAppShell>
    );
  }

  return <LoginScreen destination={destination} termsUrl={termsUrl} />;
};

export default Home;
